import useApi from 'assets/hooks/api/useApi';
import usePageRouter from 'assets/hooks/pageRouter/usePageRouter';
import { useAuthStore } from 'assets/providers/authStore/Provider.AuthStore';
import { useDataProvider } from 'assets/providers/data/DataProvider';
import { defaultLimit, defaultOffset } from 'config/Api.Config';
import contractRefundInvoiceEmailTemplateDeleteApi from 'models/core/contractRefundInvoiceEmailTemplate/delete/Api.ContractRefundInvoiceEmailTemplate.Delete';
import ContractRefundInvoiceEmailTemplate from 'models/core/contractRefundInvoiceEmailTemplate/Model.ContractRefundInvoiceEmailTemplate';
import contractRefundInvoiceEmailTemplateSaveApi from 'models/core/contractRefundInvoiceEmailTemplate/save/Api.ContractRefundInvoiceEmailTemplate.Save';
import ContractRefundInvoiceStatusTriggers from 'models/enums/ContractRefundInvoiceStatusTriggers';
import { useMemo } from 'react';
import { contractRefundInvoiceEmailTemplateIndexRoute } from './ContractRefundInvoiceEmailTemplate.Index';

export enum ContractRefundInvoiceEmailTemplateAction {
  EDIT = 'edit',
  DELETE = 'delete',
}
export type VerifyContractRefundInvoiceEmailTemplateAction = Utils.VerifyExtends<
  Module.Sales.ContractRefundInvoiceEmailTemplate.Actions,
  Utils.ValueOf<typeof ContractRefundInvoiceEmailTemplateAction>
>;
export default function useContractRefundInvoiceEmailTemplate() {
  const { permissions } = useAuthStore();
  const pageRouter = usePageRouter<
    Module.Sales.ContractRefundInvoiceEmailTemplate.Params,
    Module.Sales.ContractRefundInvoiceEmailTemplate.Query
  >({
    route: contractRefundInvoiceEmailTemplateIndexRoute,
  });

  const canView =
    permissions.CREATE_EMAILTEMPLATE || permissions.EDIT_EMAILTEMPLATE || permissions.DELETE_EMAILTEMPLATE;
  const templateApi = useApi({ action: ContractRefundInvoiceEmailTemplate.list });
  const listApi = useApi({
    action: ContractRefundInvoiceEmailTemplate.list,
    default: { limit: defaultLimit, offset: defaultOffset },
    body: pageRouter.query.list,
    callback: (req) => {
      templateApi.execute((b) => b);
      pageRouter.redirect({}, { ...pageRouter.query, list: req });
    },
    wait: !canView,
  });

  const deleteApi = useApi({
    action: contractRefundInvoiceEmailTemplateDeleteApi,
    callback: () => listApi.execute((body) => body),
    wait: true,
  });

  const saveApi = useApi({
    action: contractRefundInvoiceEmailTemplateSaveApi,
    callback: () => listApi.execute((body) => body),
    wait: true,
  });

  const selected = useMemo(() => {
    return listApi.payload?.data?.find((it) => it.id.toString() === pageRouter.params.id?.toString());
  }, [listApi.payload?.data, pageRouter.params.id]);

  const availableStatuses = useMemo(() => {
    const usedStatuses = templateApi?.payload?.data?.map((it) => it.contractRefundInvoiceStatusTrigger) || [];
    const statuses = Object.values({ ...ContractRefundInvoiceStatusTriggers })?.map((status) => status);

    return [
      ...statuses.filter((status: any) => !usedStatuses.includes(status)),
      ...[selected?.contractRefundInvoiceStatusTrigger],
    ];
  }, [templateApi?.payload?.data, selected]);

  return {
    canView,
    pageRouter,
    listApi,
    saveApi,
    deleteApi,
    selected,
    permissions,
    availableStatuses,
    views: {
      [ContractRefundInvoiceEmailTemplateAction.DELETE]: !!(
        pageRouter.params.action === ContractRefundInvoiceEmailTemplateAction.DELETE &&
        selected &&
        permissions.DELETE_EMAILTEMPLATE
      ),
      [ContractRefundInvoiceEmailTemplateAction.EDIT]: !!(
        pageRouter.params.action === ContractRefundInvoiceEmailTemplateAction.EDIT &&
        ((permissions.CREATE_EMAILTEMPLATE && !selected) || (permissions.EDIT_EMAILTEMPLATE && selected))
      ),
    },
  };
}

export function useContractRefundInvoiceEmailTemplateProvider() {
  return useDataProvider<ReturnType<typeof useContractRefundInvoiceEmailTemplate>>();
}
